import {
  Button,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  LoadingSpinner,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Typography,
} from "@/components/ui";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import type { InsuranceType } from "@/lib/data/static/matters/insuranceStatics";
import type { Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { formatInsuranceTitle } from "@/lib/utils/format";
import { getSelectableMatterInsurances } from "@/lib/utils/matters/selectable-matter-insurances";
import { PlusCircle, XIcon } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useFormContext, type UseFormRegisterReturn } from "react-hook-form";
interface SelectMatterInsuranceFormFieldProps {
  className?: string;
  emptyMessage?: string;
  isItemsError?: boolean;
  isItemsLoading?: boolean;
  label?: string;
  matter: Matter;
  contactConnectionLabel?: "Client" | "Defendant";
  partyContactId?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  ref?: React.RefObject<HTMLDivElement>;
  register: UseFormRegisterReturn;
  disabled?: boolean;
  clearable?: boolean;
  excludedInsuranceTypes?: readonly InsuranceType[];
  filterToParty?: boolean;
}

export const SelectMatterInsuranceFormField = ({
  className,
  emptyMessage,
  isItemsError,
  isItemsLoading,
  label,
  matter,
  contactConnectionLabel,
  partyContactId,
  onChange,
  placeholder,
  ref,
  register,
  disabled,
  clearable = false,
  excludedInsuranceTypes = [],
  filterToParty = true,
}: SelectMatterInsuranceFormFieldProps) => {
  const form = useFormContext();
  const registerName = register.name;
  const { openInsuranceModal } = useGlobalModal();
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  // This is needed to allow the select to be disabled when it is inside a disabled fieldset
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isInDisabledFieldset = Boolean(
    triggerRef.current?.closest("fieldset:disabled"),
  );
  const items = useMemo(
    () =>
      getSelectableMatterInsurances(
        matter,
        filterToParty ? contactConnectionLabel : undefined,
        filterToParty ? partyContactId : undefined,
      ).filter(
        (insurance) =>
          !excludedInsuranceTypes.includes(insurance.type as InsuranceType),
      ),
    [
      contactConnectionLabel,
      excludedInsuranceTypes,
      filterToParty,
      matter,
      partyContactId,
    ],
  );

  const handleCreateNewInsurance = () => {
    setIsSelectOpen(false);
    openInsuranceModal({
      matterId: matter.id,
      editing: false,
      initialTab: "details",
      requiredInsurancePartyContactId: partyContactId,
      afterCreateFn: (newInsurance) => {
        // Automatically select the newly created insurance
        onChange?.(newInsurance.data.id);
        // Also update the form field value
        form.setValue(register.name, newInsurance.data.id, {
          shouldDirty: true,
        });
      },
    });
  };

  if (!registerName) {
    return null;
  }

  return (
    <FormField
      control={form.control}
      name={registerName}
      // @ts-expect-error This line is to remove a console error
      ref={null}
      render={({ field }) => {
        const insuranceTitle = formatInsuranceTitle({
          insurance: items.find((item) => item.id === field.value),
        });
        return (
          <FormItem>
            {label && (
              <div className="flex">
                <FormLabel>{label}</FormLabel>
                <FormMessage
                  data-testid="error-message"
                  className="grow text-right"
                />
              </div>
            )}
            <Select
              {...field}
              // @ts-expect-error This line is to remove a console error
              ref={null}
              disabled={isInDisabledFieldset || disabled}
              open={isSelectOpen}
              onOpenChange={setIsSelectOpen}
              value={field.value ?? ""}
              onValueChange={(value) => {
                onChange?.(value);
                field.onChange(value, {
                  shouldDirty: true,
                });
                // Close the select dropdown after selection
                setIsSelectOpen(false);
              }}
            >
              <FormControl>
                <div className="relative">
                  <SelectTrigger
                    ref={triggerRef}
                    disabled={isInDisabledFieldset}
                    className={cn(
                      className,
                      !field.value && "text-muted-foreground",
                    )}
                  >
                    <SelectValue
                      placeholder={placeholder ?? "Select Insurance"}
                      className="w-full"
                      asChild
                    >
                      <div className="flex w-full gap-x-1">
                        <Typography weight="bold">
                          {insuranceTitle?.insurerPart}
                        </Typography>
                        <Typography>-</Typography>
                        <Typography>{insuranceTitle?.insuredPart}</Typography>
                        <Typography>-</Typography>
                        <Typography>
                          {insuranceTitle?.insuranceTypePart}
                        </Typography>
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  {clearable && field.value && (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground absolute top-1/2 right-8 -translate-y-1/2"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setValue(register.name, null, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        field.onChange(null, { shouldDirty: true });
                        onChange?.(undefined as unknown as string);
                        setIsSelectOpen(false);
                      }}
                      disabled={isInDisabledFieldset || disabled}
                    >
                      <XIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </FormControl>
              <SelectContent ref={ref}>
                <SelectGroup>
                  {items.map((item) => {
                    const insuranceTitle = formatInsuranceTitle({
                      insurance: item,
                    });
                    return (
                      <SelectItem key={item.id} value={item.id}>
                        <div className="flex gap-x-1">
                          <Typography weight="bold">
                            {insuranceTitle?.insurerPart}
                          </Typography>
                          <Typography>-</Typography>
                          <Typography>{insuranceTitle?.insuredPart}</Typography>
                          <Typography>-</Typography>
                          <Typography>
                            {insuranceTitle?.insuranceTypePart}
                          </Typography>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
                {isItemsLoading && (
                  <SelectItem
                    value="null"
                    disabled
                    className="flex justify-center"
                  >
                    <LoadingSpinner />
                  </SelectItem>
                )}
                {isItemsError && (
                  <SelectItem value="null" disabled>
                    Unable to load items.
                  </SelectItem>
                )}
                {!isItemsLoading && !isItemsError && items.length === 0 && (
                  <SelectItem value="null" disabled>
                    {emptyMessage ?? "No items available"}
                  </SelectItem>
                )}
                <div className="border-t pt-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleCreateNewInsurance();
                    }}
                  >
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Create new insurance
                  </Button>
                </div>
              </SelectContent>
            </Select>
          </FormItem>
        );
      }}
    />
  );
};
